/**
 * Subscription Management Component for AgenticRCM
 * Standalone version without Layout wrapper (no sidebar)
 * Uses SaaSFoundation API via /api proxy
 */

import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { CreditCard, CheckCircle, XCircle, Calendar, DollarSign, FileText, AlertCircle } from 'lucide-react';
import PricingModal from './PricingModal';
import InvoiceList from '../billing/InvoiceList';
import PaymentMethodManager from '../billing/PaymentMethodManager';
import UsageMetrics from '../billing/UsageMetrics';
import { getSubscriptionTrialInfo, formatTrialDaysLeft } from '../../utils/subscriptionTrial';

const SubscriptionManagement = () => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('overview');
  const [showPricing, setShowPricing] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get API base URL - uses nginx proxy to SaaSFoundation
  // Always use nginx proxy (/api/v1) except when running SaaSFoundation app directly on localhost:3000/3001
  const getApiBaseUrl = () => {
    if (typeof window !== 'undefined' && window.location) {
      const port = window.location.port;
      const hostname = window.location.hostname;
      
      // Only for direct development (SaaSFoundation app itself), use explicit port
      // For AgenticRCM/AgenticPractice, always use nginx proxy
      if (hostname === 'localhost' && (port === '3000' || port === '3001')) {
        // This is SaaSFoundation app itself, use direct backend
        return 'http://localhost:8001/api/v1';
      }
      
      // For all other cases (containerized, production, AgenticRCM/AgenticPractice), use nginx proxy
      return '/api/v1'; // nginx proxies /api to SaaSFoundation:8001
    }
    
    // Fallback: use nginx proxy
    return '/api/v1';
  };

  const API_BASE_URL = getApiBaseUrl();
  
  // Log API base URL on mount for debugging
  useEffect(() => {
    console.log('[SubscriptionManagement] API_BASE_URL:', API_BASE_URL);
    console.log('[SubscriptionManagement] Window location:', window.location.href);
  }, []);

  const fetchSubscription = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      const url = `${API_BASE_URL}/subscriptions/current`;
      
      console.log('[SubscriptionManagement] Fetching subscription from:', url);
      console.log('[SubscriptionManagement] Has token:', !!token);
      
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      console.log('[SubscriptionManagement] Response status:', response.status);
      console.log('[SubscriptionManagement] Response ok:', response.ok);

      if (!response.ok) {
        // Handle 403/404 as "no subscription" rather than error
        if (response.status === 403 || response.status === 404) {
          console.log('[SubscriptionManagement] No subscription found (403/404)');
          setSubscriptionData(null);
          setLoading(false);
          return;
        }
        const errorText = await response.text();
        console.error('[SubscriptionManagement] HTTP error:', response.status, errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('[SubscriptionManagement] Subscription data received:', data);
      
      // Handle empty subscription response
      if (data && (!data.subscription || data.subscription === null)) {
        console.log('[SubscriptionManagement] Empty subscription in response');
        setSubscriptionData(null);
      } else {
        setSubscriptionData(data);
      }
    } catch (err) {
      console.error('[SubscriptionManagement] Error fetching subscription:', err);
      // Don't show error for 403/404 - just treat as no subscription
      if (err.message && !err.message.includes('403') && !err.message.includes('404')) {
        setError(err.message);
      } else {
        setSubscriptionData(null);
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle URL parameters for direct navigation (like SaaSFoundation)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    const invoiceId = params.get('invoice');
    
    if (tab) {
      setActiveTab(tab);
    }
    
    // If invoice ID is provided, ensure we're on billing tab
    if (invoiceId && tab !== 'billing') {
      setActiveTab('billing');
    }
  }, [location.search]);

  useEffect(() => {
    fetchSubscription();
  }, []);

  const handlePlanChange = async (plan, billingCycle) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      
      const response = await fetch(`${API_BASE_URL}/subscriptions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          plan_id: plan.id,
          billing_cycle: billingCycle,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to change plan');
      }

      await fetchSubscription();
      setShowPricing(false);
      alert(`Successfully ${subscriptionData?.subscription?.plan?.name === 'free' ? 'upgraded' : 'changed'} to ${plan.display_name || plan.name}!`);
    } catch (error) {
      console.error('Failed to change plan:', error);
      alert(error.message || 'Failed to change plan. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? It will remain active until the end of your current period.')) {
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      
      const response = await fetch(`${API_BASE_URL}/subscriptions/${subscriptionData.subscription.id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        throw new Error('Failed to cancel subscription');
      }

      await fetchSubscription();
      alert('Subscription cancelled. It will remain active until the end of your current period.');
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
      alert('Failed to cancel subscription. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePayInvoice = async (invoiceId) => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      
      const response = await fetch(`${API_BASE_URL}/invoices/${invoiceId}/pay`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        if (response.status === 400) {
          alert('Please add a payment method first.');
          setActiveTab('payment');
          return;
        }
        throw new Error('Failed to pay invoice');
      }

      await fetchSubscription();
      alert('Invoice paid successfully!');
    } catch (error) {
      console.error('Failed to pay invoice:', error);
      alert(error.message || 'Failed to pay invoice. Please try again.');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
          <p className="text-gray-600">Loading subscription...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center space-x-2">
          <AlertCircle className="w-5 h-5 text-red-600" />
          <p className="text-red-800">Error loading subscription: {error}</p>
        </div>
        <button
          onClick={fetchSubscription}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!subscriptionData || !subscriptionData.subscription) {
    return (
      <div className="space-y-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Subscription Management</h1>
            <p className="text-gray-600 mt-2">Manage your subscription and billing</p>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <CreditCard className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">No Active Subscription</h2>
          <p className="text-gray-600 mb-6">You don't have an active subscription yet. Choose a plan to get started.</p>
          <button
            onClick={() => setShowPricing(true)}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            View Plans & Subscribe
          </button>
        </div>

        {/* Pricing Modal */}
        {showPricing && (
          <PricingModal
            currentPlanId={null}
            onSelectPlan={handlePlanChange}
            onClose={() => setShowPricing(false)}
            API_BASE_URL={API_BASE_URL}
          />
        )}
      </div>
    );
  }

  const { subscription, usage_metrics, recent_invoices, payment_methods } = subscriptionData;
  const trial = getSubscriptionTrialInfo(subscription);

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'usage', label: 'Usage' },
    { id: 'billing', label: 'Invoices' },
    { id: 'payment', label: 'Payment Methods' },
  ];

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Subscription Management</h1>
          <p className="text-gray-600 mt-2">Manage your subscription and billing</p>
        </div>
        <button
          onClick={() => setShowPricing(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Change Plan
        </button>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-8">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Current Plan Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Current Plan</h3>
                  <p className="text-sm text-gray-600 mt-1">{subscription.plan.display_name || subscription.plan.name}</p>
                </div>
                <div className="text-right">
                  {trial.isTrial ? (
                    <>
                      <p className="text-2xl font-bold text-gray-900">Free trial</p>
                      <p className="text-sm text-gray-600">
                        {trial.trialLengthDays ? `${trial.trialLengthDays}-day trial` : 'No charge during trial'}
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-2xl font-bold text-gray-900">
                        {formatCurrency(subscription.plan.price)}
                      </p>
                      <p className="text-sm text-gray-600">
                        per {subscription.billing_cycle || 'month'}
                      </p>
                    </>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mt-6">
                <div>
                  <p className="text-sm text-gray-600">Status</p>
                  <p className={`text-sm font-medium mt-1 ${
                    trial.isTrial ? 'text-blue-600' : subscription.status === 'active' ? 'text-green-600' : 'text-gray-600'
                  }`}>
                    {trial.isTrial ? (
                      <span className="flex items-center">
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Trial
                      </span>
                    ) : subscription.status === 'active' ? (
                      <span className="flex items-center">
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Active
                      </span>
                    ) : (
                      <span className="flex items-center">
                        <XCircle className="w-4 h-4 mr-1" />
                        {subscription.status}
                      </span>
                    )}
                  </p>
                  {trial.isTrial && trial.daysRemaining != null ? (
                    <p className={`text-sm mt-1 ${trial.daysRemaining <= 7 ? 'text-amber-700 font-medium' : 'text-gray-600'}`}>
                      {formatTrialDaysLeft(trial.daysRemaining)}
                    </p>
                  ) : null}
                </div>
                <div>
                  <p className="text-sm text-gray-600">{trial.isTrial ? 'Trial ends' : 'Next Billing Date'}</p>
                  <p className="text-sm font-medium mt-1">
                    {formatDate(trial.isTrial ? trial.trialEndsAt : subscription.current_period_end)}
                  </p>
                </div>
              </div>

              {/* Plan Features */}
              {subscription.plan.features && subscription.plan.features.length > 0 && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Plan Features</h4>
                  <ul className="space-y-2">
                    {subscription.plan.features.map((feature, index) => (
                      <li key={index} className="flex items-center text-sm text-gray-600">
                        <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-4">
              {!subscription.cancel_at_period_end && subscription.plan.name !== 'free' && (
                <button
                  onClick={handleCancelSubscription}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  disabled={loading}
                >
                  Cancel Subscription
                </button>
              )}
              <button
                onClick={() => setShowPricing(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                {subscription.plan.name === 'free' ? 'Upgrade Plan' : 'Change Plan'}
              </button>
            </div>
          </div>
        )}

        {activeTab === 'usage' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-6">Current Usage</h3>
            <UsageMetrics 
              usageMetrics={usage_metrics || []} 
              planLimits={subscription?.plan?.limits || {}} 
            />
          </div>
        )}

        {activeTab === 'billing' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Invoices</h3>
            <p className="text-sm text-gray-500 mb-6">Full billing history for your organization.</p>
            <InvoiceList autoFetch apiBaseUrl={API_BASE_URL} />
          </div>
        )}

        {activeTab === 'payment' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <PaymentMethodManager />
          </div>
        )}
      </div>

      {/* Pricing Modal */}
      {showPricing && (
        <PricingModal
          currentPlanId={subscription?.plan?.id}
          onSelectPlan={handlePlanChange}
          onClose={() => setShowPricing(false)}
          API_BASE_URL={API_BASE_URL}
        />
      )}
    </div>
  );
};

export default SubscriptionManagement;

